import React, { useState, useMemo, useCallback, useEffect } from 'react';
import CardList from '../components/CardList';
import MapView from '../components/MapView';
import SidebarRail from '../components/SidebarRail';
import HeroSection from '../components/HeroSection';
import { CardData } from '../types';

export const HomePage: React.FC = () => {
    // States
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [viewMode, setViewMode] = useState<'cards' | 'map'>('cards');
    const [isDesktopMapCollapsed, setIsDesktopMapCollapsed] = useState<boolean>(false);
    const [selectedCard, setSelectedCard] = useState<CardData | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [sortBy, setSortBy] = useState<'popular' | 'name' | 'area'>('popular');
    const [cards, setCards] = useState<CardData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    // Screen size watcher
    useEffect(() => {
        const checkIsMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    // Fetch cards data from Azure Functions API
    useEffect(() => {
        const fetchCards = async () => {
            try {
                setLoading(true);
                const isLocal = typeof window !== 'undefined' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

                // On Cloudflare Pages or in production, use our same-origin edge proxy (/api/cards) to eliminate CORS issues
                // In local development, direct call to Azure works because localhost is in Azure's CORS allowed origins
                const defaultEndpoint = isLocal 
                    ? 'https://gujarati-catalogue-function.azurewebsites.net/api/azure-function-gujarati-catalolue'
                    : '/api/cards';

                const apiEndpoint = process.env.REACT_APP_USE_DIRECT_AZURE === 'true'
                    ? (process.env.REACT_APP_API_ENDPOINT || defaultEndpoint)
                    : (isLocal ? (process.env.REACT_APP_API_ENDPOINT || defaultEndpoint) : '/api/cards');

                const functionKey = process.env.REACT_APP_FUNCTION_KEY;

                const headers: HeadersInit = {
                    'Content-Type': 'application/json',
                };

                // Only attach function key when making direct requests to Azure (not needed via same-origin edge function)
                if (functionKey && !apiEndpoint.startsWith('/')) {
                    headers['x-functions-key'] = functionKey;
                }

                let response = await fetch(apiEndpoint, {
                    method: 'GET',
                    headers,
                });

                // Fallback: If edge proxy returns error and direct Azure endpoint is configured, attempt direct fetch
                if (!response.ok && apiEndpoint.startsWith('/') && process.env.REACT_APP_API_ENDPOINT) {
                    const fallbackHeaders: HeadersInit = { 'Content-Type': 'application/json' };
                    if (functionKey) fallbackHeaders['x-functions-key'] = functionKey;
                    try {
                        const directResponse = await fetch(process.env.REACT_APP_API_ENDPOINT, {
                            method: 'GET',
                            headers: fallbackHeaders,
                        });
                        if (directResponse.ok) {
                            response = directResponse;
                        }
                    } catch (fallbackErr) {
                        console.warn('Fallback direct fetch also failed:', fallbackErr);
                    }
                }

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                const transformedData: CardData[] = data.map((item: any, idx: number) => ({
                    id: item.id || `card-${idx}-${item.name || ''}`,
                    name: item.name || '',
                    contact: item.contact || '',
                    area: item.area || '',
                    comments: item.comments || '',
                    fullAddress: item.fullAddress || '',
                    googleMapLocation: item.googleMapLocation || '',
                    isCateringServiceAvailable: item.isCateringServiceAvailable || '',
                    businessName: item.businessName || '',
                    category: item.category || 'Food Service',
                    imageUrl: item.imageUrl || '',
                }));

                setCards(transformedData);
                setError(null);
            } catch (err) {
                console.error('Error fetching data from API:', err);

                if (err instanceof Error) {
                    if (err.message.includes('401')) {
                        setError('Authentication failed. Please check your API configuration.');
                    } else if (err.message.includes('403')) {
                        setError('Access denied. Please verify your API permissions.');
                    } else if (err.message.includes('429')) {
                        setError('Too many requests. Please try again later.');
                    } else {
                        setError('Failed to load data from live backend. Loaded verified directory catalog.');
                    }
                }

                // Verified offline fallback data matching catalogue listings
                setCards([
                    {
                        id: '1',
                        name: 'Krishnaben Jora',
                        businessName: 'Angel Food Services',
                        contact: '88844 66640',
                        area: 'Munnekolal',
                        fullAddress: 'PURVI KHOSALA Silver spring road, Munnekolal, Bengaluru, 560037',
                        googleMapLocation: 'https://maps.app.goo.gl/7VJP6TQrLSFx3Hk8A',
                        isCateringServiceAvailable: 'Yes',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1610057099443-fde8c4d50f91?auto=format&fit=crop&w=400&q=80',
                    },
                    {
                        id: '2',
                        name: 'VK Catering & Tiffin Service',
                        businessName: 'VK Catering & Tiffin Service',
                        contact: '95388 47788',
                        area: 'BTM Layout',
                        fullAddress: 'Survey Number 13/3 Mahendra, 2M Layout, BTM 2nd Stage, Bengaluru',
                        googleMapLocation: 'https://maps.google.com/?q=BTM+Layout+Bengaluru',
                        isCateringServiceAvailable: 'Yes',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=400&q=80',
                    },
                    {
                        id: '3',
                        name: 'Jalaram Caterers',
                        businessName: 'Jalaram Caterers',
                        contact: '97407 94846',
                        area: 'Prakash Nagar',
                        fullAddress: '7th Main, 6th D Cross Rd, Prakash Nagar, Bengaluru, 560021',
                        googleMapLocation: 'https://maps.google.com/?q=Prakash+Nagar+Bengaluru',
                        isCateringServiceAvailable: 'Yes',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=400&q=80',
                    },
                    {
                        id: '4',
                        name: 'Chandrika Tiffans',
                        businessName: 'Chandrika Tiffans',
                        contact: '80957 28735, 76762 37875',
                        area: 'Kadugodi',
                        fullAddress: 'Behind Udipi cafe, Whitefield Hoskote highway road, Kadugodi, 560067',
                        googleMapLocation: 'https://maps.google.com/?q=Kadugodi+Bengaluru',
                        isCateringServiceAvailable: 'Yes',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=400&q=80',
                    },
                    {
                        id: '5',
                        name: 'Vibhahen Pujara',
                        businessName: 'Vibhahen Pujara',
                        contact: '82005 05090',
                        area: 'Bengaluru',
                        fullAddress: 'Business name not provided, Bengaluru',
                        googleMapLocation: '',
                        isCateringServiceAvailable: 'No',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=400&q=80',
                    },
                    {
                        id: '6',
                        name: 'Rupalben Shah',
                        businessName: 'Rupalben Shah',
                        contact: '99009 63412',
                        area: 'Bengaluru',
                        fullAddress: 'Business name not provided, Bengaluru',
                        googleMapLocation: '',
                        isCateringServiceAvailable: 'No',
                        category: 'Food Services',
                        imageUrl: 'https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=400&q=80',
                    },
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchCards();
    }, []);

    // Derived unique categories
    const categories = useMemo(() => {
        const allCategories = cards.flatMap(card => {
            if (!card.category) return [];
            return card.category.split(/\s*,\s*/).filter(cat => cat.trim().length > 0);
        });

        const uniqueCategories = Array.from(new Set(allCategories))
            .filter(cat => cat.length > 0)
            .sort((a, b) => a.localeCompare(b));

        return ['All', ...uniqueCategories];
    }, [cards]);

    // Count listings per category
    const categoryCounts = useMemo(() => {
        const counts: Record<string, number> = { 'All': cards.length };

        categories.forEach(category => {
            if (category !== 'All') {
                counts[category] = cards.filter(card => {
                    if (!card.category) return false;
                    const cardCategories = card.category.split(/\s*,\s*/);
                    return cardCategories.some(cat => cat.trim() === category);
                }).length;
            }
        });

        return counts;
    }, [cards, categories]);

    // Multi-token smart search & category filtering
    const filteredCards = useMemo(() => {
        let result = cards;

        // Apply Category filter
        if (selectedCategory !== 'All') {
            result = result.filter(card => {
                if (!card.category) return false;
                const cardCategories = card.category.split(/\s*,\s*/).map(cat => cat.trim());
                // Exact match or partial inclusion
                return cardCategories.some(cat => cat.toLowerCase() === selectedCategory.toLowerCase()) ||
                    card.category.toLowerCase().includes(selectedCategory.toLowerCase());
            });
        }

        // Apply Multi-token Search filter
        if (searchQuery.trim()) {
            const tokens = searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean);
            result = result.filter(card => {
                const searchableText = `${card.name} ${card.businessName || ''} ${card.area || ''} ${card.fullAddress || ''} ${card.category || ''} ${card.contact || ''}`.toLowerCase();
                return tokens.every(token => searchableText.includes(token));
            });
        }

        // Apply Sorting
        if (sortBy === 'name') {
            result = [...result].sort((a, b) => {
                const nameA = a.businessName || a.name || '';
                const nameB = b.businessName || b.name || '';
                return nameA.localeCompare(nameB);
            });
        } else if (sortBy === 'area') {
            result = [...result].sort((a, b) => {
                const areaA = a.area || a.fullAddress || '';
                const areaB = b.area || b.fullAddress || '';
                return areaA.localeCompare(areaB);
            });
        }

        return result;
    }, [cards, selectedCategory, searchQuery, sortBy]);

    // Handlers
    const handleCardClick = useCallback((card: CardData) => {
        setSelectedCard(card);
        // On mobile, switch to map view centered on this card
        if (isMobile) {
            setViewMode('map');
        }
    }, [isMobile]);

    const handleMarkerClick = useCallback((card: CardData | null) => {
        setSelectedCard(card);
        // On desktop, scroll card into view
        if (card && !isMobile) {
            const cardElem = document.querySelector(`[data-card="${card.name}"]`);
            if (cardElem) {
                cardElem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }
    }, [isMobile]);

    const toggleDesktopMap = useCallback(() => {
        setIsDesktopMapCollapsed(prev => !prev);
    }, []);

    return (
        <div className="max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Hero Section with Search & Illustration */}
            <HeroSection
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCategory={selectedCategory}
                onCategorySelect={setSelectedCategory}
                categories={categories}
                categoryCounts={categoryCounts}
            />

            {/* Main Content Layout with Left Sidebar Rail */}
            <div className="flex gap-6 lg:gap-8 items-start">
                {/* Left Navigation Sidebar Rail (Desktop only) */}
                <div className="hidden md:block sticky top-20">
                    <SidebarRail
                        selectedCategory={selectedCategory}
                        onSelectCategory={setSelectedCategory}
                        availableCategories={categories}
                    />
                </div>

                {/* Main Directory Area */}
                <div className="flex-1 min-w-0">
                    {/* Top Control Bar: Total Count, Sorting, View Switcher */}
                    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl p-4 mb-5 border border-orange-100/60 dark:border-gray-800 shadow-soft flex flex-wrap items-center justify-between gap-3">
                        {/* Title & Count */}
                        <div className="flex items-center gap-2">
                            <h2 className="text-lg sm:text-xl font-black text-gray-900 dark:text-white tracking-tight">
                                {filteredCards.length}+ Gujarati Places
                            </h2>
                            {selectedCategory !== 'All' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300">
                                    {selectedCategory}
                                </span>
                            )}
                        </div>

                        {/* Controls on Right */}
                        <div className="flex items-center gap-3">
                            {/* Sort Dropdown */}
                            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 font-medium">
                                <span className="hidden sm:inline text-gray-400">Sort by:</span>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value as any)}
                                    className="bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                >
                                    <option value="popular">Popular</option>
                                    <option value="name">Name (A-Z)</option>
                                    <option value="area">Area</option>
                                </select>
                            </div>

                            {/* Desktop: Collapse/Expand Map Button */}
                            <div className="hidden lg:block">
                                <button
                                    onClick={toggleDesktopMap}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all duration-200 flex items-center gap-1.5 shadow-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-orange-400"
                                >
                                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                                    </svg>
                                    <span>{isDesktopMapCollapsed ? 'Show Map' : 'Hide Map'}</span>
                                </button>
                            </div>

                            {/* Mobile: View Switcher Pill */}
                            <div className="flex lg:hidden bg-gray-100 dark:bg-gray-700/60 p-0.5 rounded-xl border border-gray-200 dark:border-gray-600">
                                <button
                                    onClick={() => setViewMode('cards')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                                        viewMode === 'cards'
                                            ? 'bg-orange-500 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-300'
                                    }`}
                                >
                                    Cards
                                </button>
                                <button
                                    onClick={() => setViewMode('map')}
                                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                                        viewMode === 'map'
                                            ? 'bg-orange-500 text-white shadow-sm'
                                            : 'text-gray-600 dark:text-gray-300'
                                    }`}
                                >
                                    Map
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Notice / Fallback Info Banner */}
                    {error && (
                        <div className="bg-amber-50/90 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/60 text-amber-800 dark:text-amber-300 px-4 py-2.5 rounded-2xl text-xs mb-4 flex items-center justify-between shadow-sm">
                            <span className="flex items-center gap-2">
                                <span>ℹ️</span>
                                <span>{error}</span>
                            </span>
                            <button onClick={() => setError(null)} className="text-amber-700 dark:text-amber-400 font-semibold hover:underline ml-2">
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* Content Section: Loading, Error, or Listings */}
                    {loading ? (
                        <div className="flex flex-col justify-center items-center h-72 bg-white dark:bg-gray-800 rounded-3xl p-8 border border-orange-100/60 dark:border-gray-700 shadow-soft">
                            <div className="w-12 h-12 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mb-4"></div>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                Loading Bangalore Gujarati directory...
                            </p>
                        </div>
                    ) : filteredCards.length === 0 ? (
                        <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 text-center border border-orange-100/60 dark:border-gray-700 shadow-soft">
                            <div className="w-16 h-16 rounded-2xl bg-orange-100 dark:bg-orange-950/40 text-orange-600 flex items-center justify-center text-3xl mx-auto mb-3">
                                🔍
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                                No places found matching "{searchQuery || selectedCategory}"
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                                Try searching for another keyword or reset your category filters.
                            </p>
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setSelectedCategory('All');
                                }}
                                className="px-4 py-2 rounded-xl bg-orange-500 text-white font-bold text-xs shadow-sm hover:bg-orange-600 transition-colors"
                            >
                                Reset All Filters
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View (Split or Full Width depending on map collapse) */}
                            <div className="hidden lg:grid grid-cols-12 gap-6 items-start">
                                {/* Cards Column */}
                                <div
                                    className={`${
                                        isDesktopMapCollapsed
                                            ? 'col-span-12'
                                            : 'col-span-6 xl:col-span-5 h-[calc(100vh-14rem)] overflow-y-auto pr-1'
                                    }`}
                                >
                                    <CardList
                                        cards={filteredCards}
                                        selectedCard={selectedCard}
                                        onCardClick={handleCardClick}
                                        onViewOnMap={handleCardClick}
                                        layout={isDesktopMapCollapsed ? 'grid' : 'stack'}
                                    />
                                </div>

                                {/* Map Column (Sticky on right) */}
                                {!isDesktopMapCollapsed && (
                                    <div className="col-span-6 xl:col-span-7 sticky top-20 h-[calc(100vh-14rem)] min-h-[550px]">
                                        <MapView
                                            cards={filteredCards}
                                            selectedCard={selectedCard}
                                            onMarkerClick={handleMarkerClick}
                                            totalCategoriesCount={categories.length - 1}
                                            className="h-full"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Mobile View (Cards and Map View toggled with CSS so Map is never destroyed) */}
                            <div className="block lg:hidden">
                                <div className={viewMode === 'cards' ? 'block' : 'hidden'}>
                                    <CardList
                                        cards={filteredCards}
                                        selectedCard={selectedCard}
                                        onCardClick={handleCardClick}
                                        onViewOnMap={handleCardClick}
                                        layout="stack"
                                    />
                                </div>
                                <div className={viewMode === 'map' ? 'block h-[75vh] min-h-[500px]' : 'hidden'}>
                                    <MapView
                                        cards={filteredCards}
                                        selectedCard={selectedCard}
                                        onMarkerClick={handleMarkerClick}
                                        totalCategoriesCount={categories.length - 1}
                                        className="h-full"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default HomePage;